import { resolveBrowserContextRequestUrl } from './playwright-client/request-url.js';
import {
  fetchWithNativeRequestCookies,
  mergeNativeRequestCookies,
  type NativeRequestFetchResult,
} from './playwright-client/native-request-fetch.js';
import type { PwCookie } from './set-cookie-merge.js';

export type ApiRequestOptions = {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
};

/** Minimal authenticated HTTP transport used by api_sequence execution. */
export interface ApiRequestClient {
  fetch(options: ApiRequestOptions): Promise<NativeRequestFetchResult>;
}

export type ScopedRequestHeaders = {
  /** Exact URL origin. Headers are never sent to another scheme, host, or port. */
  origin: string;
  headers: Record<string, string>;
};

export type ApiRequestStorageState = {
  cookies: PwCookie[];
  origins?: unknown[];
};

export type CreateApiRequestClientOptions = {
  storageState: ApiRequestStorageState;
  /** Authenticated page/login URL used to resolve relative API request paths. */
  baseUrl?: string | null;
  /** Credential headers applied to an explicit request and same-origin redirects only. */
  defaultHeaders?: Record<string, string>;
  /** Secrets such as Cloudflare Access or HTTP Basic scoped to one exact origin. */
  scopedHeaders?: ScopedRequestHeaders[];
};

function normalizedOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Create an authenticated native-fetch client detached from Playwright.
 *
 * The client owns an in-memory copy of the storage-state cookies, reconciles
 * Set-Cookie responses between steps, and recalculates scoped headers for every
 * redirect target. It remains usable after the browser that produced the state
 * has disconnected.
 */
export function createApiRequestClient(options: CreateApiRequestClientOptions): ApiRequestClient {
  let cookies = options.storageState.cookies.map((cookie) => ({ ...cookie }));
  const scopedHeaders = (options.scopedHeaders ?? []).flatMap((entry) => {
    const origin = normalizedOrigin(entry.origin);
    return origin ? [{ origin, headers: { ...entry.headers } }] : [];
  });

  return {
    fetch: (request) => {
      const requestUrl = resolveBrowserContextRequestUrl(request.url, options.baseUrl ?? null);
      const requestOrigin = normalizedOrigin(requestUrl);
      return fetchWithNativeRequestCookies({
        requestUrl,
        method: request.method,
        headers: request.headers,
        body: request.body,
        timeoutMs: request.timeoutMs,
        signal: request.signal,
        cookieJar: {
          cookies: async () => cookies,
          applySetCookies: async (incoming) => {
            cookies = mergeNativeRequestCookies(cookies, incoming);
          },
        },
        defaultHeadersForUrl: (targetUrl) => {
          const targetOrigin = normalizedOrigin(targetUrl);
          const headers: Record<string, string> =
            targetOrigin && targetOrigin === requestOrigin ? { ...options.defaultHeaders } : {};
          if (!targetOrigin) return headers;
          for (const rule of scopedHeaders) {
            if (rule.origin === targetOrigin) Object.assign(headers, rule.headers);
          }
          return headers;
        },
      });
    },
  };
}
